import {useCallback, type PropsWithChildren} from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Close from '@mui/icons-material/Close';
import Button from 'react-bootstrap/Button';
import {useSelector} from 'react-redux';
import {type SystemNotification} from '@qep/common';
import {selectSystemNotifications} from '../../../state/reducer';
import './system-notification-modal.scss';

type SystemNotificationWithAcknowledged = SystemNotification & {acknowledged: boolean};

type SystemNotificationAction = {
  label: string;
  href: string;
};

const actionRelativeBase = 'https://qep-system-notification.invalid';

/** Returns supported external or same-origin action URLs and rejects executable or ambiguous schemes. */
function getSafeActionHref(rawHref: string): string | undefined {
  const href = rawHref.trim();
  try {
    const url = new URL(href, actionRelativeBase);
    if (href.startsWith('/') || href.startsWith('\\')) {
      return url.origin === actionRelativeBase ? href : undefined;
    }

    return url.protocol === 'http:' || url.protocol === 'https:' ? href : undefined;
  } catch {
    return undefined;
  }
}

const style = {
  position: 'absolute',
  top: '40%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 425,
  height: 300,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  outline: 'none',
  p: 0,
};

const titleStyles = {
  width: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  padding: 2,
  fontSize: 22,
};

const messageStyles = {
  width: '100%',
  flexGrow: 1,
  overflow: 'auto',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  px: 2,
  fontSize: 18,
  height: '100%',
};

const actionStyles = {
  width: '100%',
  justifyContent: 'flex-end',
  alignItems: 'center',
  p: 2,
};

const closeStyles = {
  position: 'absolute',
  height: 31,
  width: 31,
  right: 0,
};

type Props = {
  open: boolean;
  notificationId?: string;
  handleClose: (notificationId?: string) => void;
};

/** Displays one stored system notification while keeping subscription-provided content inert. */
export function SystemNotificationModal({open, handleClose, notificationId}: PropsWithChildren<Props>) {
  /** Closes a critical notification and reports its identifier so the caller can acknowledge it. */
  const acknowledge = useCallback(() => {
    handleClose(notificationId);
  }, [handleClose, notificationId]);
  /** Closes a non-critical notification without acknowledging it. */
  const dismiss = () => {
    handleClose();
  };

  const notifications = useSelector(selectSystemNotifications);

  const notification = notifications.find((n: SystemNotificationWithAcknowledged) => n.id === notificationId);
  if (!notification) {
    return null;
  }

  const acknowledgeRequired = notification.level === 'critical';

  return (
    <Modal
      open={open}
      onClose={acknowledgeRequired ? undefined : dismiss}
      disableEscapeKeyDown
    >
      <Box sx={style}>
        <Stack direction='row' sx={{height: '100%'}}>
          <Stack direction='column' sx={{width: '100%'}}>
            <Box sx={{...titleStyles, color: acknowledgeRequired ? '#f00' : '#f80'}}>{notification.title}</Box>
            <Box sx={messageStyles}>
              <div>{String(notification.message)}</div>
            </Box>

            <Stack sx={actionStyles} direction='row'>
              { }
              {/* Converts each server-provided action into a safe external-link button. */}
              {notification.actions?.map((action: SystemNotificationAction) => {
                const actionLabel = String(action.label ?? '');
                const actionHref = getSafeActionHref(String(action.href ?? ''));
                if (!actionHref) {
                  return null;
                }

                return (
                  <Button
                    key={actionLabel}
                    size='lg'
                    variant='secondary'
                    className='browser-system-notification-secondary-button'
                    href={actionHref}
                    target='_blank'
                    rel='noreferrer'
                  >{actionLabel}</Button>
                );
              })}
              {acknowledgeRequired
                ? <Button className='browser-system-notification-close-button' size='lg' onClick={acknowledge}>Acknowledge</Button>
                : <Button className='browser-system-notification-close-button' size='lg' onClick={dismiss}>Close</Button>}
            </Stack>
          </Stack>

          {acknowledgeRequired
            ? null
            : <IconButton sx={closeStyles} onClick={dismiss}><Close /></IconButton>}
        </Stack>
      </Box>
    </Modal>
  );
}
